import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  LogOut, BarChart3, Pencil, BookOpenCheck, GraduationCap,
  ClipboardList, FileSignature, BookOpen, MessageCircleQuestion, Trophy, Users, ScanText, RefreshCw, TrendingUp, Crown
} from "lucide-react";
import ThemeToggle from "./ThemeToggle";
import Brand from "./Brand";

const baseNav = [
  { to: "/app", label: "Progress Tracker", icon: BarChart3, end: true },
  { to: "/app/paths", label: "Growth Paths", icon: TrendingUp },
  { to: "/app/revision", label: "Revision Intelligence", icon: RefreshCw },
  { to: "/app/conference", label: "Writing Conference", icon: MessageCircleQuestion },
  { to: "/app/practice", label: "Writing Practice", icon: Pencil },
  { to: "/app/grammar", label: "Grammar Lab", icon: BookOpenCheck },
  { to: "/app/sat-ap", label: "SAT/AP Prep", icon: GraduationCap },
  { to: "/app/essay", label: "Essay Coach", icon: FileSignature },
  { to: "/app/breakdown", label: "Prompt Breakdown", icon: ScanText },
  { to: "/app/constructed", label: "Constructed Response", icon: ClipboardList },
  { to: "/app/rubric", label: "Rubric Feedback", icon: BookOpen },
  { to: "/app/coach", label: "Guided Coach", icon: MessageCircleQuestion },
  { to: "/app/examples", label: "Examples", icon: BookOpen },
  { to: "/app/badges", label: "Badges", icon: Trophy },
];

function navForRole(role) {
  if (role === "teacher") {
    return [
      { to: "/app/roster", label: "Classroom Roster", icon: Users },
      ...baseNav,
    ];
  }
  if (role === "parent") {
    return [
      { to: "/app/roster", label: "Family Roster", icon: Users },
      { to: "/app/parent", label: "Parent Hub", icon: Users },
      ...baseNav,
    ];
  }
  // student
  return [
    ...baseNav,
    { to: "/app/roster", label: "My Coaches", icon: Users },
    { to: "/app/parent", label: "Parent Hub", icon: Users },
  ];
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const onLogout = () => { logout(); navigate("/"); };
  const navItems = navForRole(user?.role);

  return (
    <div className="min-h-screen bg-slate-50">
      <header data-testid="app-header" className="bg-white/80 backdrop-blur-xl border-b border-slate-200/60 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Brand to="/app" tone="light" size="md" testId="brand-link" />
          <div className="flex items-center gap-3">
            {user?.founder_badge && (
              <span data-testid="founder-badge" className="hidden sm:inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-amber-400 to-orange-400 text-slate-900 px-3 py-1 text-xs font-bold uppercase tracking-wider shadow-sm">
                <Crown size={11}/> Founding Member
              </span>
            )}
            {user?.is_premium ? (
              <span data-testid="premium-badge" className="hidden sm:inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 px-3 py-1 text-xs font-bold uppercase tracking-wider">
                <Crown size={11}/> Premium
              </span>
            ) : (
              <Link to="/app/upgrade" data-testid="upgrade-pill" className="hidden sm:inline-flex items-center gap-1 rounded-full bg-amber-400 hover:bg-amber-300 text-slate-900 px-3 py-1 text-xs font-bold uppercase tracking-wider">
                <Crown size={11}/> Upgrade
              </Link>
            )}
            <span data-testid="user-grade-badge" className="hidden sm:inline-flex rounded-full bg-slate-100 text-slate-700 px-3 py-1 text-xs font-bold uppercase tracking-wider">
              {user?.grade_level} • {user?.role}
            </span>
            <span data-testid="user-name" className="hidden md:inline text-sm text-slate-600">Hi, {user?.name}</span>
            <ThemeToggle />
            <button onClick={onLogout} data-testid="logout-button" className="rounded-full px-4 py-2 text-sm font-bold border-2 border-slate-200 hover:border-slate-300 flex items-center gap-2">
              <LogOut size={14}/> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-8">
        <aside data-testid="sidebar" className="lg:sticky lg:top-20 lg:self-start">
          <nav className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
            {navItems.map((it) => {
              const Icon = it.icon;
              return (
                <NavLink
                  key={it.to}
                  to={it.to}
                  end={it.end}
                  data-testid={`nav-${it.label.toLowerCase().replace(/[\s\/]+/g,'-')}`}
                  className={({isActive}) => `flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-semibold whitespace-nowrap transition-all ${isActive ? 'bg-indigo-600 text-white shadow-[0_4px_0_0_rgb(67,56,202)]' : 'bg-white border-2 border-slate-100 hover:border-indigo-200 text-slate-700'}`}
                >
                  <Icon size={16} />
                  {it.label}
                </NavLink>
              );
            })}
          </nav>
        </aside>
        <main>{children}</main>
      </div>
    </div>
  );
}
